import { createApi } from '@reduxjs/toolkit/query/react';
import { UserProfile, UserResponse } from '@handover/shared-types';
import { END_POINTS, METHOD } from '../../constants/constant';
import { customUserBaseQuery } from '../customBaseQuery';

export type UserDeleteImpact = {
  departureOwnerTransfers: number;
  departureIncomingTransfers: number;
  incomingViewTransfers: number;
  archivedHandovers: number;
  transferredHandovers: number;
  requiresWarning: boolean;
  freesSeat: boolean;
};

export type UserDeletePreviewResponse = {
  targetUser: {
    email: string;
    role?: string;
  };
  impact: UserDeleteImpact;
};

export type DeleteUserResponse = {
  message?: string;
  impact: Omit<UserDeleteImpact, 'requiresWarning' | 'freesSeat'>;
  authUserDeleted?: boolean;
  authDeleteError?: string | null;
};

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: customUserBaseQuery,
  tagTypes: ['Me', 'Users'],
  endpoints: builder => ({
    // Query to fetch the current user's profile
    getMe: builder.query<UserResponse, void>({
      query: () => ({
        url: END_POINTS.userme,
        method: METHOD.GET,
      }),
      providesTags: ['Me'],
    }),
    // Query to fetch all users' profiles
    getAllUser: builder.query<UserResponse, void>({
      query: () => ({
        url: END_POINTS.userall,
        method: METHOD.GET,
      }),
      providesTags: ['Users'],
    }),
    // Mutation to update a user's profile
    updateUser: builder.mutation<UserResponse, UserProfile>({
      query: data => ({
        url: END_POINTS.userupdate,
        method: METHOD.POST,
        body: data,
      }),
      invalidatesTags: ['Me', 'Users'],
    }),
    previewDeleteUser: builder.mutation<UserDeletePreviewResponse, { email: string }>({
      query: body => ({
        url: END_POINTS.userdeletepreview,
        method: METHOD.POST,
        body,
      }),
    }),
    deleteUser: builder.mutation<DeleteUserResponse, { email: string }>({
      query: body => ({
        url: END_POINTS.userdelete,
        method: METHOD.POST,
        body,
      }),
      invalidatesTags: ['Users', 'Me'],
    }),
  }),
});

// Export hooks for each endpoint
export const {
  useGetMeQuery,
  useGetAllUserQuery,
  useUpdateUserMutation,
  usePreviewDeleteUserMutation,
  useDeleteUserMutation,
} = userApi;
